export type EVENT_TYPE = 'storage' | 'SyncStorageEvent' | 'InternalStorageEvent';

const SYNC_SET_ACTION = 'set';
const SYNC_REMOVE_ACTION = 'remove';
type SyncAction = 'set' | 'remove';

interface SyncData {
  action: SyncAction;
  value?: string;
}

// console.info(`p5-context(${document.location.pathname}) new.`);

/**
 * This event is used to sync instances emitted if there is a change notification from other instances (other browser tabs or windows).
 *
 * This interface uses the same main properties from the native {@link StorageEvent}
 * to simplify the usage and keep backward comparability.
 *
 * To help listener determine the event type easier,
 * {@link InternalStorageEvent.type} is also provided with value 'SyncStorageEvent'.
 * {@link StorageEvent.type} is always 'storage'.
 */
export class SyncStorageEvent {
  public readonly type: EVENT_TYPE = 'SyncStorageEvent';

  constructor(public readonly key: string,
    public readonly oldValue: string,
    public readonly newValue: string,
    public readonly storageArea: Storage) {
  }
}

/**
 * The native StorageEvent is only emitted by the window storage event. (using addEventListener).
 * For emitting our own local events the InternalStorageEvent is used.
 *
 * For that purpose, the methods {@link DbContext.set} and {@link DbContext.remove} will emit {@link InternalStorageEvent}
 * in the local windows and in the remorte windows.
 *
 * This interface uses the same main properties from the native {@link StorageEvent}
 * to simplify the usage and keep backward comparability.
 *
 * To help listener determine the event type easier,
 * {@link InternalStorageEvent.type} is also provided with value 'InternalStorageEvent'.
 * {@link StorageEvent.type} is always 'storage'.
 */
export class InternalStorageEvent {
  public readonly type: EVENT_TYPE = 'InternalStorageEvent';

  constructor(public readonly key: string,
    public readonly oldValue: string,
    public readonly newValue: string,
    public readonly storageArea: Storage = sessionStorage) {
  }
}

/**
 * Instead of manage events using window.addEventListener('storage') directly,
 * all context relevant events are emitted to ContextEventManager to centralize context events management.
 * Using this class, we can manage both native {@link StorageEvent} and our self created events.
 */
class ContextEventManager {

  // all registered listeners are held in this object.
  // It is not an array to enable unregistration without renumbering. 
  private listeners = {};

  /** used to create a unique number for all registered listeners. */
  private counter = 0;

  emit(event: StorageEvent | SyncStorageEvent | InternalStorageEvent) {
    if (this.counter > 0) {
      // if (event.key.substr(0, 3) === 'db-')
      //   debugger;
      // console.info(`p5-context(${document.location.pathname}) ==> ${event.key} = ${event.newValue}`);

      Object.keys(this.listeners).forEach(listenerId => {
        try {
          this.listeners[listenerId](event);
        } catch (e) {
          console.error(`NO EMIT to ${listenerId}`);
        }
      });
    }
  }

  /**
   * @return the registered listener's id. It allows us remove the listener when needed.
   */
  listen(callback: (event: StorageEvent | SyncStorageEvent | InternalStorageEvent) => void): number {
    this.listeners[this.counter++] = callback;
    // console.info(`p5-context(${document.location.pathname}) add listener # ${this.counter}`);
    return this.counter;
  }

  removeListener(id): boolean {
    return delete this.listeners[id];
  }
}

/**
 * To prevent confusion, below is the explanation of the naming convention in this class:
 * - contextKey: is a key without prefix 'db-context.'. Example: user.email
 * - prefixedKey: the contextKey with the prefix 'db-context.'. Example: db-context.user.email
 * - prefixedSyncKey: the contextKey with the prefix 'db-context-sync.'. Example: db-context-sync.user.email
 * - storageKey: the actual key string in localStorage and sessionStorage of browser.
 *   Example: db-context.user.email, db-context-sync.user.email, or any.key.in.storage
 * - instance: every time a window object (new tab, new browser window or frame) is opened,
 *   a new participant in the context is created.
 */
export class DbContext {

  // region fields declaration

  /**
   * The instanceKey uniquely identifies one participant on the context. This related to a HTML window/frame not to a real window.
   * There is one sessionStorage per real window and all window/frame objects inside share the same sessionStore.
   * @private
   * @memberof DbContext
   */
  private instanceKey;

  /**
   * The instance value holds the old values of the context keys for this instance.
   * The old values for this instance cannot to be retrieved from the storage because it might have been changed by another instance of the same window.
   * @private
   * @memberof DbContext
   */
  private instanceValues = {};

  /**
   * Context key must have the prefix 'db-context.' when it is stored in storage.
   */
  readonly KEY_PREFIX = 'db-context.';

  /**
   * After the new instance created and has an instance id,
   * it must register its instance id in the instance list as value of the key 'db-context-instances' in {@link localStorage}.
   */
  readonly REGISTERED_INSTANCES_KEY = 'db-context-instances';

  /**
   * If context key (ex. 'user.email') in sessionStorage has a new value or is removed,
   * the change is propagated to the sync key (ex. 'db-context-sync.user.email') in {@link localStorage}.
   *
   * Each context key will be synced to separated sync key to prevent the case of race condition conflict of multiple keys.
   */
  readonly SYNC_KEY_PREFIX = 'db-context-sync.';

  /**
   * If an instance (ex. instance-a) is created,
   * it should copied the current state from an existing instance (ex. instance-b).
   *
   * To ask for a sync from an existing instance,
   * it (instance-a) sets any non-nil value to the key 'db-context-sync-from.instance-b' in {@link localStorage}.
   */
  readonly SYNC_FROM_KEY_PREFIX = 'db-context-sync-from.';

  /**
   * The default storage for context entries is using the {@link sessionStorage}
   * that is kept in memory only and will be purged when the window or tab is closed.
   * The specialKeys contains the list of all key treated differently.
   * 'P' means that this value is allowed to be stored persistently in {@link localStorage}.
   *     and will be available even after the browser was closed and restarted.
   * 'W' means that this value is only available in one window/tab.
   */
  readonly SPECIAL_DEFAULT = 'S';
  readonly SPECIAL_PERSIST = 'P';
  readonly SPECIAL_WINDOW = 'W';
  // #BPCP-172
  private readonly specialKeys = {
    'test.check': this.SPECIAL_PERSIST,
    'portal.language': this.SPECIAL_PERSIST,
    'db-notification.info': this.SPECIAL_WINDOW,
    'db-notification.success': this.SPECIAL_WINDOW,
    'db-notification.warning': this.SPECIAL_WINDOW,
    'db-notification.error': this.SPECIAL_WINDOW
  };


  private readonly contextEventManager = new ContextEventManager();

  /**
   * This flag indicate if the current instance has done the initialization process
   * (Ex. copy the current {@link sessionStorage} from an existing instance).
   */
  private initialized = false;

  /**
   * To start the initialization, the current instance need to find an existing instance to ask for a sync from that instance.
   * If that instance is found, it is stored in this field.
   */
  private initializedFromInstance: number;

  private syncBlacklist: string[] = [];

  // endregion

  /**
   * Setup event listeners to handle sync events between instances.
   *
   * @param syncBlacklist initialize the sync blacklist.
   *        This list contains the context keys that should not notify about their changes to other instances.
   */
  constructor(syncBlacklist?: string[]) {

    this.syncInstanceValuesWithSessionStorage();

    this.setSyncBlacklist(syncBlacklist);

    // Initialize the instance. The sessionStorage may already be in use.
    this.instanceKey = Date.now().toString();

    // Each time new instance is created, we refresh the current list and register itself to the list.
    this.refreshInstanceList();

    // Delay after refresh to make sure all existing instances are registered again.
    setTimeout(() => {
      const existingInstance = this.getAnotherExistingInstance();
      if (this.isNotNil(existingInstance)) {
        this.initializedFromInstance = existingInstance;
        localStorage.setItem(
          this.getPrefixedSyncFromInstanceKey(existingInstance),
          `Request a sync from the existing instance ${existingInstance} at timestamp: ${Date.now()}`
        );
      } else {
        // no existing other instance is found => initialization is not needed.
        this.initialized = true;

        // clean left-over entries in localStorage
        let keys = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (this.startsWith(key, this.SYNC_KEY_PREFIX))
            keys.push(key);
          if (this.startsWith(key, this.SYNC_FROM_KEY_PREFIX))
            keys.push(key);
        } // for
        keys.forEach((k) => { localStorage.removeItem(k) });
      }
    }, 500);

    // handle native StorageEvent here
    window.addEventListener('storage', (event: StorageEvent) => {
      let key = event.key;
      let eventKey = key.split('.')[0] + '.';
      let isHandled = false;

      if (event.storageArea == sessionStorage) {
        // no interest in sessionStorage events.
        // no action here.
        // #BPCP-172
        const contextKey = this.getContextKeyFromPrefixedKey(key);
        // contextKeys marked as window local are not emitted when
        // comming from the session Storage not using the localstorage.
        if (this.isWindowLocal(contextKey)) {
          const newEvent = new InternalStorageEvent(contextKey, event.oldValue, event.newValue);
          this.contextEventManager.emit(newEvent);
        }

      } else if (eventKey === this.SYNC_KEY_PREFIX) {
        // this is a sync because a context value was changed elswhere.
        isHandled = this.handleIncomingSyncEvent(event);

      } else if (eventKey === this.KEY_PREFIX) {
        // entries using the local store need to be emitted now. using the Event Manager.
        const contextKey = this.getContextKeyFromPrefixedKey(key);
        if (this.isNotNil(contextKey)) {
          const newEvent = new InternalStorageEvent(contextKey, event.oldValue, event.newValue);
          this.contextEventManager.emit(newEvent);
        }
        this.instanceValues[contextKey] = event.newValue;
        isHandled = true;

      } else if (eventKey === this.SYNC_FROM_KEY_PREFIX) {
        // handle the sync requests when another window is opened.
        isHandled = this.handleInitializationEvent(event);
        if (!isHandled)
          isHandled = this.handleSyncRequestEvent(event);


      } else if (key === this.REGISTERED_INSTANCES_KEY) {
        // refreshing the list of active instances.
        this.registerInstance();
        isHandled = true;
      } // if
    }); // addEventListener('storage'...)


    window.addEventListener('unload', () => {
      // Refresh the instance list (by simply clear the list) without register itself when unload current instance.
      // Note that, if a tab is crashed, this event could not be triggered!
      localStorage.removeItem(this.REGISTERED_INSTANCES_KEY);
    });
  }

  // region public methods

  /**
   * Get current value of context key.
   * @example <code>get('user.email')</code> // => 'john.doe@db.com'
   * @param contextKey the context key to be checked. Ex. 'user.email'.
   * @returns the current value in storage.
   *          <code>null</code> if the given key is not exist
   *          (according to the {@link https://developer.mozilla.org/en-US/docs/Web/API/Storage/getItem|Web Storage API}).
   */
  get(contextKey: string): string | undefined {
    let val = this.getStorage(contextKey).getItem(this.getPrefixedKey(contextKey));
    return ((val === null) ? undefined : val);
  } // get()

  /**
   * Get all current values of existing context keys in their correspondent storage.
   *
   * Example usages:
   * <code>getAll('user.email')<code>
   * // => {'user.email': 'john.doe@db.com'}
   *
   * <code>getAll(['user.email', 'user.last_login'])</code>
   * // => {'user.email': 'firstname.lastname@db.com', 'user.last_login': '1511523472858'}
   *
   * <code>getAll(contextKey => contextKey.startWith('user.'))</code>
   * // => {'user.email': 'firstname.lastname@db.com', 'user.last_login': '1511523472858', 'user.family_name': 'doe'}
   *
   * <code>
   *     const userEntries = getAll(contextKey => contextKey.startWith('user.'));
   *     Object.keys(userEntries).foreach(contextKey => {
   *         console.log(contextKey + ' : ' + userEntries[contextKey]); // Ex. 'user.email : john.doe@db.com'
   *     });
   * </code>
   *
   * @param keyFilter use to filter context keys in result.
   * @return the current relevant context keys and correspondent values in correspondent storage.
   *          Only return the context keys that satisfy the given <code>keyFilter</code>.
   *          Return all existing context keys if the given <code>keyFilter</code> is <code>null</code>.
   */
  getAll(keyFilter?: ((contextKey: string) => boolean) | string | string[]): { [contextKey: string]: string | null } {
    if (this.isNil(keyFilter)) {
      return this._getAll();
    }

    return this._getAll(contextKey => {
      let keyMatched = false;
      if (this.isNotNil(contextKey)) {
        if (typeof keyFilter === 'string') {
          keyMatched = keyFilter === contextKey;
        } else if (keyFilter instanceof Array) {
          keyMatched = this.includes(keyFilter, contextKey);
        } else {
          keyMatched = keyFilter(contextKey);
        }
      }
      return keyMatched;
    });
  }

  /**
   * Update value for context key in its correspondent storage.
   * Remove the context key if the given value is provided as <code>null</code> or <code>undefined</code>.
   * Emit an {@link InternalStorageEvent} if there is an actual change in storage.
   *
   * @param contextKey the context key to be checked. Ex. 'user.email'.
   * @param value the new value will be saved for the given contextKey.
   *              If <code>null</code> or <code>undefined</code> is provided, it will be removed from storage.
   * @return <code>true</code> if the value is removed or updated.
   *          <code>false</code> if there is no actual change in storage (Ex. old value equals the given value).
   */
  set(contextKey: string, value: string | null | undefined): boolean {
    // console.info(`p5-context(${document.location.pathname}) <== ${contextKey} = ${value}`);

    if (this.isNil(value)) {
      return (this.remove(contextKey));
    }

    // Get old value for this instance.
    const oldValue = this.instanceValues[contextKey];

    const changed = (oldValue !== value);
    if (changed) {
      const storageArea = this.getStorage(contextKey);
      const prefixedKey = this.getPrefixedKey(contextKey);

      // always set the storage value before sending the event.
      storageArea.setItem(prefixedKey, value);

      // send out to all local listeners
      const internalStorageEvent = new InternalStorageEvent(contextKey, oldValue, value, storageArea);
      this.contextEventManager.emit(internalStorageEvent);

      // if it is not localStorage: sync to other windows.
      const isWL = this.isWindowLocal(contextKey);
      if (isWL) {
        // do not send around // #BPCP-172
      } else if (storageArea === sessionStorage) {
        this.doSyncEntry(internalStorageEvent);
      }
      this.instanceValues[contextKey] = value;
    } // if

    return (changed);
  } // set()


  /**
   * Remove the context key from its correspondent storage.
   * Emit an {@link InternalStorageEvent} if there is an actual change in storage.
   *
   * @param contextKey the context key to be checked. Ex. 'user.email'.
   * @return <code>true</code> if the value is removed. <code>false</code> if the given key does not exist in storage.
   */
  remove(contextKey: string): boolean {

    // Get old value for this instance.
    const oldValue = this.instanceValues[contextKey];

    const changed = this.isNotNil(oldValue);
    if (changed) {
      const storageArea = this.getStorage(contextKey);
      const prefixedKey = this.getPrefixedKey(contextKey);

      storageArea.removeItem(prefixedKey);
      // send out to all local listeners
      const internalStorageEvent = new InternalStorageEvent(contextKey, oldValue, undefined, storageArea);
      this.contextEventManager.emit(internalStorageEvent);

      // if it is not localStorage: sync to other windows.
      const isWL = this.isWindowLocal(contextKey);
      if (isWL) {
        // do not send around // #BPCP-172
      } else if (storageArea === sessionStorage) {
        this.doSyncEntry(internalStorageEvent);
      }

    } // if
    this.instanceValues[contextKey] = null;

    return changed;
  }

  /**
   * Listen to change events of context keys in their correspondent storage.
   * Only listen to the context keys that satisfy the given <code>keyFilter</code>.
   *
   * Example usages:
   * <code>listen('user.email', callback)</code>
   * <code>listen(['user.email', 'user.last_login'], callback)</code>
   * <code>listen(contextKey => contextKey.startWith('user.'), callback)</code>
   *
   * @param keyFilter determine which context keys should be listened to.
   * @param callback trigger the callback when received a change event of context key in its correspondent storage.
   *                 Note that the <code>event.key</code> has prefix while the <code>contextKey</code> has no prefix.
   * @param ignoreInternalStorageEvent If <code>true</code>, {@link InternalStorageEvent} is not emitted to the listener.
   *                                   Default is <code>true</code> to maintain backward-compatibility.
   */
  listen(
    keyFilter: ((contextKey: string) => boolean) | string | string[],
    callback: (event: StorageEvent | SyncStorageEvent | InternalStorageEvent, contextKey: string) => void
  ): void {

    this.contextEventManager.listen(event => {
      let keyMatched = false;
      const key = event.key;

      if (this.isNotNil(key)) {
        if (typeof keyFilter === 'string') {
          keyMatched = (keyFilter === key);
        } else if (keyFilter instanceof Array) {
          keyMatched = this.includes(keyFilter, key);
        } else {
          keyMatched = keyFilter(key);
        }
      } // if

      if (keyMatched) {
        callback(event, key);
      } // if
    });
  }

  /**
   * @return the current sync blacklist.
   *         This list contains the context keys that should not notify about their changes to other instances.
   */
  getSyncBlacklist(): string[] {
    // return a copy to prevent unexpected change to the internal state.
    return [...this.syncBlacklist];
  }

  /**
   * Set the sync blacklist to the list of the given context keys, remove the old list.
   *
   * @param contextKeys the list of context keys, that should not notify about their changes to other instances.
   *                    If the given context keys is <code>null</code> or <code>undefined</code>, the sync blacklist is set to empty.
   */
  setSyncBlacklist(contextKeys: string[] | null | undefined): void {
    this.syncBlacklist = this.isNil(contextKeys) ? [] : contextKeys;
  }

  /**
   * Add the given context keys to the current sync blacklist.
   *
   * @param contextKeys the list of context keys, that should not notify about their changes to other instances.
   *                    If the given context keys is <code>null</code> or <code>undefined</code>, the sync blacklist is not changed.
   */
  addToSyncBlacklist(contextKeys: string[] | null | undefined): void {
    if (this.isNil(contextKeys) || contextKeys.length === 0) {
      return;
    }

    contextKeys.filter(contextKey => !this.includes(this.syncBlacklist, contextKey))
      .forEach(contextKey => this.syncBlacklist.push(contextKey));
  }

  /**
   * Remove the given context keys from the current sync blacklist.
   *
   * @param contextKeys the list of context keys, that should not notify about their changes to other instances.
   *                    If the given context keys is <code>null</code> or <code>undefined</code>, the sync blacklist is not changed.
   */
  removeFromSyncBlacklist(contextKeys: string[] | null | undefined): void {
    if (this.isNil(contextKeys) || contextKeys.length === 0) {
      return;
    }

    this.syncBlacklist = this.syncBlacklist.filter(contextKey => !this.includes(contextKeys, contextKey));
  }

  // endregion

  /**
   * In this private method, we can filter not only by context key but also storage of the context key for internal usage.
   * The public method {@link DbContext.getAll} does not expose the storage information to external.
   * @private
   */
  private _getAll(keyFilter?: ((contextKey: string, storage: Storage) => boolean)): { [contextKey: string]: string | null } {
    const result = {};

    if (this.isNil(keyFilter)) {
      keyFilter = () => true;
    }

    const keyMatchedFn = (contextKey: string, storage: Storage) => {
      return this.isNotNil(contextKey)
        && storage === this.getStorage(contextKey)
        && keyFilter(contextKey, storage);
    };

    // traverse localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i);
      const contextKey = this.getContextKeyFromPrefixedKey(storageKey);
      if (keyMatchedFn(contextKey, localStorage)) {
        result[contextKey] = localStorage.getItem(storageKey);
      }
    }

    // traverse sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const storageKey = sessionStorage.key(i);
      const contextKey = this.getContextKeyFromPrefixedKey(storageKey);
      if (keyMatchedFn(contextKey, sessionStorage)) {
        result[contextKey] = sessionStorage.getItem(storageKey);
      }
    }

    return result;
  }


  // region instances management

  /**
   * Refresh current instance list by clear the list and register current instance again.
   * Other instances will be notified about this event and register themself.
   * This way, the instance list becomes up to date.
   */
  private refreshInstanceList() {
    // first issue a change with non-empty
    localStorage.setItem(this.REGISTERED_INSTANCES_KEY, "*");
    // the remove the key to trigger collecting all active instance keys
    localStorage.removeItem(this.REGISTERED_INSTANCES_KEY);
    // register myself
    this.registerInstance();
  }

  /**
   * If the current instance is not registered, register it to the instance list.
   */
  private registerInstance() {
    const instances = this.getRegisteredInstances();

    if (!this.includes(instances, this.instanceKey)) {
      instances.push(this.instanceKey);
      // sorting will improve stability because the leader instance is most likely the oldest instance consistently.
      instances.sort();
      localStorage.setItem(this.REGISTERED_INSTANCES_KEY, JSON.stringify(instances));
    } // if
  } // registerInstance()


  /**
   * @return the first instance in the current instance list (exclude itself).
   *          <code>undefined</code> if no other instance is found.
   */
  private getAnotherExistingInstance(): number | undefined {
    const existingInstances = this.getRegisteredInstances()
      .filter(registeredInstance => registeredInstance !== this.instanceKey);
    return existingInstances.length > 0
      ? existingInstances[0]
      : undefined;
  }


  /**
   * @return the current instance list.
   *          Return an empty array if the list does not exist or the value in {@link localStorage} is invalid.
   */
  private getRegisteredInstances(): number[] {
    let instances: number[] = [];
    const oldValue = localStorage.getItem(this.REGISTERED_INSTANCES_KEY);
    try {
      const oldInstances = JSON.parse(oldValue);
      if (oldInstances instanceof Array) {
        instances = oldInstances;
      }
    } catch (e) {
      // invalid json, ignore
    }
    return instances;
  }

  // endregion

  // region event handling


  /**
   * Send out a localStorage based sync to other possible interested instances.
   * @private
   * @param {InternalStorageEvent} event The event with change of the context value.
   * @returns {void} 
   * @memberof DbContext
   */
  private doSyncEntry(event: InternalStorageEvent): void {
    // - If a context key in sessionStorage is changed, notify other instances about this change.
    // - Only trigger this notification after initialized.
    // - A SyncStorageEvent happened only when receive notification from other instances.
    //   Therefore, do not trigger another redundant notification for this event type.

    const syncData: SyncData = this.isNil(event.newValue)
      ? { action: SYNC_REMOVE_ACTION }
      : { action: SYNC_SET_ACTION, value: event.newValue };

    this.triggerSync(event.key, syncData);
  } // doSyncEntry()


  /**
   * @return true if the initialization is done and handled.
   */
  private handleInitializationEvent(event: StorageEvent): boolean {
    // check if the initialization is done, set the initialized to true.

    const isRemoveSyncFromEventInLocalStorage =
      this.isNotNil(this.initializedFromInstance)
      && this.isNilOrEmpty(event.newValue)
      && this.getPrefixedSyncFromInstanceKey(this.initializedFromInstance) === event.key;
    if (!this.initialized && isRemoveSyncFromEventInLocalStorage) {
      this.initialized = true;
      return true;
    }

    return (false);
  } // handleInitializationEvent()

  /**
   * If other instances ask current instance for a sync,
   * trigger the sync for all current context keys in sessionStorage of current instance.
   * @return true if the sync request is served.
   */
  private handleSyncRequestEvent(event: StorageEvent): boolean {
    const isInitSyncFromEventInLocalStorage =
      (!this.isNilOrEmpty(event.newValue))
      && this.getPrefixedSyncFromInstanceKey(this.instanceKey) === event.key;
    if (isInitSyncFromEventInLocalStorage) {
      // Trigger sync for the whole context keys in sessionStorage
      const sessionContextEntries = this._getAll((_contextKey, storage) => storage === sessionStorage);
      Object.keys(sessionContextEntries).forEach(_contextKey => {
        this.triggerSync(_contextKey, { action: SYNC_SET_ACTION, value: sessionContextEntries[_contextKey] });
      });
      localStorage.removeItem(this.getPrefixedSyncFromInstanceKey(this.instanceKey));
      return true;
    } // if

    return false;
  } // handleSyncRequestEvent()


  /**
   * Handle incoming sync events using key like 'db-context-sync.instaceID.user.email'.
   * Events are only 
   * @private
   * @param {StorageEvent} event 
   * @returns {boolean} true if the incoming sync event is handled.
   * @memberof DbContext
   */
  private handleIncomingSyncEvent(event: StorageEvent): boolean {
    let key: string = event.key;

    // only process events from localstorage
    let keyParts: Array<string> = event.key.split('.');

    // only process events from other instances
    if (keyParts[1] != this.instanceKey) {
      try {
        const contextKey = keyParts.slice(2).join('.');
        const prefixedKey = this.getPrefixedKey(contextKey);
        const store = this.getStorage(contextKey);

        // Get old value for this instance.
        const oldValue = this.instanceValues[contextKey];

        const syncData: SyncData = JSON.parse(event.newValue);

        // If a sync notification is received, receivers update the correspondent context key in sessionStorage.
        let syncValue = syncData['value'];
        if (syncValue === oldValue) {
          // do not emit this.
        } else if (syncData['action'] === SYNC_REMOVE_ACTION || this.isNil(syncValue)) {
          store.removeItem(prefixedKey);
          const newEvent = new InternalStorageEvent(contextKey, oldValue, undefined);
          // const syncEvent = new SyncStorageEvent(prefixedKey, oldValue, null, sessionStorage);
          this.contextEventManager.emit(newEvent);
          this.instanceValues[contextKey] = null;

        } else if (syncData['action'] === SYNC_SET_ACTION) {
          store.setItem(prefixedKey, syncValue);
          const newEvent = new InternalStorageEvent(contextKey, oldValue, syncValue);
          // const syncEvent = new SyncStorageEvent(prefixedKey, oldValue, syncValue, sessionStorage);
          this.contextEventManager.emit(newEvent);
          this.instanceValues[contextKey] = syncValue;
        }

      } catch (e) {
        // invalid json or error, ignore
      }

    } // if
    // }


    return (true);
  }

  /**
   * If there is a change of context key in sessionStorage, update the correspondent syncKey to notify other instances about the change.
   * The notifier will not trigger another redundant sync because we do not emit internal event when update syncKey.
   * @return <code>true</code> if the sync is triggered.
   *         Otherwise, return <code>false</code> (Ex. the given context key is in sync blacklist).
   */
  private triggerSync(contextKey: string, syncData: SyncData): void {
    // Do not trigger sync if it is not enabled globaly or the context key is in the sync blacklist
    if (!this.includes(this.syncBlacklist, contextKey)) {
      // Make a change to the localstorage using the SYNC_KEY_PREFIX including the local Instance ID. 
      // and revoke the change immediately. 
      const prefixedSyncKey = this.getPrefixedSyncKey(contextKey);
      localStorage.setItem(prefixedSyncKey, JSON.stringify(syncData));
      localStorage.removeItem(prefixedSyncKey);
    } // if
  } // triggerSync()

  // endregion

  // region utilities to handle key string

  /**
   * Example usages:
   * <code>getStorage('user.email')</code> // => {@link sessionStorage}
   * <code>getStorage('test.check')</code> // => {@link localStorage}
   * @returns the storage where the given context key should be stored in.
   */
  private getStorage(contextKey: string): Storage {
    const entry = this.specialKeys[contextKey];
    let store = sessionStorage; // default

    if (this.isNotNil(entry) && (entry === this.SPECIAL_PERSIST)) {
      store = localStorage;
    } // if
    return (store);
  }


  /**
   * #BPCP-172
   */
  private isWindowLocal(contextKey: string): boolean {
    const entry = this.specialKeys[contextKey];

    if (this.isNotNil(entry) && (entry === this.SPECIAL_WINDOW)) {
      return (true);
    } // if
    return (false);
  }

  /**
   * Example usage: <code>getPrefixedKey('user.email')</code> // => 'db-context.user.email'
   *
   * @return the context key with the prefix {@link KEY_PREFIX}.
   */
  private getPrefixedKey(contextKey: string): string {
    return this.KEY_PREFIX + contextKey;
  }

  /**
 * Example usage: <code>getPrefixedKey('user.email')</code> // => 'db-context.user.email'
 *
 * @return the context key with the prefix {@link KEY_PREFIX}.
 */
  private removePrefixedKey(key: string): string {
    return key.replace(this.KEY_PREFIX, '');
  }


  /**
   * Example usages:
   * <code>isPrefixedKey('db-context-sync.user.email')</code> // => false
   * <code>isPrefixedKey('db-context.user.email')</code> // => true
   *
   * @param storageKey the actual key in storage.
   * @return <code>true</code> if the given <code>storageKey</code> has prefix {@link KEY_PREFIX}
   */
  private isPrefixedKey(storageKey: string): boolean {
    return this.startsWith(storageKey, this.KEY_PREFIX);
  }

  /**
   * @return the context key after extract the prefix {@link KEY_PREFIX} from the given <code>prefixedKey</code>.
   *          Return <code>undefined</code> if the given <code>prefixedKey</code> is not prefixed with {@link KEY_PREFIX}.
   */
  private getContextKeyFromPrefixedKey(prefixedKey: string): string | undefined {
    if (this.isPrefixedKey(prefixedKey)) {
      return prefixedKey.substr(this.KEY_PREFIX.length);
    }

    return undefined;
  }

  /**
   * Example usage: <code>getPrefixedSyncKey('user.email')</code> // => 'db-context-sync.instaceID.user.email'
   *
   * @return the context key with the prefix {@link SYNC_KEY_PREFIX}.
   */
  private getPrefixedSyncKey(contextKey: string): string {
    return this.SYNC_KEY_PREFIX + this.instanceKey + "." + contextKey;
  }

  /**
   * Example usages:
   * <code>isPrefixedSyncKey('db-context.user.email')</code> // => false
   * <code>isPrefixedSyncKey('db-context-sync.user.email')</code> // => true
   *
   * @param storageKey the actual key in storage.
   * @return <code>true</code> if the given <code>storageKey</code> has prefix {@link SYNC_KEY_PREFIX}
   */
  private isPrefixedSyncKey(storageKey: string): boolean {
    return this.startsWith(storageKey, this.SYNC_KEY_PREFIX);
  }

  /**
   * @return the context key after extract the prefix {@link SYNC_KEY_PREFIX} from the given <code>prefixedSyncKey</code>.
   *          Return <code>undefined</code> if the given <code>prefixedSyncKey</code> is not prefixed with {@link SYNC_KEY_PREFIX}.
   */
  private getContextKeyFromPrefixedSyncKey(prefixedSyncKey: string): string | undefined {
    if (this.isPrefixedSyncKey(prefixedSyncKey)) {
      let val = prefixedSyncKey.substr(this.SYNC_KEY_PREFIX.length);
      return (val);
    }

    return undefined;
  }

  /**
   * Example usage: <code>getPrefixedSyncFromInstanceKey('1511523472858')</code> // => 'db-context-sync-from.1511523472858'
   *
   * @return the context key with the prefix {@link SYNC_FROM_KEY_PREFIX}.
   */
  private getPrefixedSyncFromInstanceKey(instance: number): string {
    return this.SYNC_FROM_KEY_PREFIX + instance;
  }

  /**
   * Example usages:
   * <code>isPrefixedSyncFromInstanceKey('db-context.user.email')</code> // => false
   * <code>isPrefixedSyncFromInstanceKey('db-context-sync-from.1511523472858')</code> // => true
   *
   * @param storageKey the actual key in storage.
   * @return <code>true</code> if the given <code>storageKey</code> has prefix {@link SYNC_FROM_KEY_PREFIX}
   */
  private isPrefixedSyncFromInstanceKey(storageKey: string): boolean {
    return this.startsWith(storageKey, this.SYNC_FROM_KEY_PREFIX);
  }

  // endregion

  // region general useful utilities

  /**
   * Check if the given <code>value</code> is nil to avoid confusing when using <code>null</code> or <code>undefined</code>.
   * @return <code>true</code> if the given <code>value</code> is <code>null</code> or <code>undefined</code>.
   */
  private isNil(value: any): boolean {
    return value === undefined || value === null;
  }

  /**
   * Check if the given <code>value</code> is NOT nil to avoid confusing when using <code>null</code> or <code>undefined</code>.
   * @return <code>true</code> if the given <code>value</code> is neither <code>null</code> nor <code>undefined</code>.
   */
  private isNotNil(value: any): boolean {
    return !this.isNil(value);
  }

  /**
   * Check if the given <code>value</code> is <code>null</code>, <code>undefined</code> or an empty string.
   * @private
   * @param {*} value the given value to be analyzed.
   * @returns {boolean} <code>true</code> if the given <code>value</code> is neither <code>null</code> nor <code>undefined</code>.
   * @memberof DbContext
   */
  private isNilOrEmpty(value: any): boolean {
    return ((value === undefined) || (value === null) || (value === ""));
  } // isNilOrEmpty()


  /**
   * @return <code>true</code> if the given <code>str</code> starts with the given <code>subStr</code>.
   *         Otherwise, return <code>false</code>.
   *         Return <code>false</code> also if one of the given params is <code>null</code> or <code>undefined</code>.
   */
  private startsWith(str: string, subStr: string): boolean {
    if (this.isNil(str) || this.isNil(subStr)) {
      return false;
    }

    return str.length >= subStr.length && str.substr(0, subStr.length) === subStr;
  }

  /**
   * @return true if the given value is included in the given array.
   */
  private includes(arr: any[], value: any): boolean {
    if (this.isNil(arr)) {
      return false;
    }

    return arr.some(_value => value === _value);
  }

  /**
   * Syncs the current array of Instance values with the current SessionStorage as after pressing F5 there is
   * a deviation of the instances
   * 
   * TODO: Check if one way check is enough: As far as at the moment is known only comparsion from SessionStage to InstanceValues
   * is needed
   */
  private syncInstanceValuesWithSessionStorage(): void {
    const currentSessionStorageKeys = Object.keys(sessionStorage);

    currentSessionStorageKeys.forEach(key =>
      this.instanceValues[this.removePrefixedKey(key)] = sessionStorage[key]
    );
  }

  // endregion

  /**
   * Return a list with all actual values in context storages.
   * Please use the method {@link DbContext.getAll} instead.
   * 
   * @returns {({ key: string; value: string | null; storage: 'local' | 'session' }[])} 
   * @return 
   * @memberof DbContext
   */
  list(): { key: string; value: string | null; storage: 'local' | 'session' }[] {
    const prefixLen = this.KEY_PREFIX.length;
    const result = [];

    // traverse the local storage
    for (const i in localStorage) {
      if (localStorage.hasOwnProperty(i)) {
        if (i.substr(0, prefixLen) === this.KEY_PREFIX) {
          const dbKey = i.substring(prefixLen);
          result.push({
            key: dbKey,
            value: localStorage.getItem(i),
            storage: 'local'
          });
        } // if
      } // if
    } // for

    // traverse the session storage
    for (const i in sessionStorage) {
      if (sessionStorage.hasOwnProperty(i)) {
        if (i.substr(0, prefixLen) === this.KEY_PREFIX) {
          const dbKey = i.substring(prefixLen);
          result.push({
            key: dbKey,
            value: sessionStorage.getItem(i),
            storage: 'session'
          });
        } // if
      } // if
    } // for

    return (result);
  }

  // endregion
}



// WEBPACK FOOTER //
// ./~/source-map-loader!./src/context.ts